import { Injectable } from 'angular-ts-decorators';

import { Domain } from '@shared/app/scripts/services/domain';
import type { IConfig } from '@shared/app/scripts/services/config';
import type { IStandardErrorParser } from '@shared/app/scripts/services/standard-error-parser';

/**
 * Factory in the coreModule. It takes care of communication with the token
 * resource.
 */
@Injectable('token')
export class Token {
  private readonly spynlUrl: string;
  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private standardErrorParser: IStandardErrorParser,
    domain: Domain,
    config: IConfig,
  ) {
    'ngInject';

    this.spynlUrl = `//${domain.sibling(config.spynlSubdomain)}`;
  }

  /**
   * Endpoint used to request Spynl tokens. Note: These tokens are not the same
   * as Spynl sessions.
   * @param {string} description
   * A token description written by the user, saved on the token object
   * @param {string} tenantId
   * Optional tenantId
   * @returns {promise} resolves with object containing the response of the http request.
   * Reject with an error message.`
   */
  request(description: string, tenantId?: string): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return (
      this.$http
        .post(`${this.spynlUrl}${tenantSection}/tokens/request`, {
          description,
        })
        .catch(this.standardErrorParser)
        // TODO CORE-440 move error to catch
        .then(
          (response) => response.data,
          (error) => this.$q.reject(error.data.message),
        )
    );
  }

  /**
   *
   * Endpoint used to revokes a spynl token. Note: These tokens are not the same as Spynl sessions.
   * @param {string} tokenId
   * Spynl Token Id
   * @param {string} tenantId
   * Optional tenantId
   * @returns {promise} resolves with object containing the response of the http request.
   * Reject with an error message.`
   */
  revoke(tokenId: string, tenantId?: string): ng.IPromise<any> {
    let tenantSection = '';

    if (angular.isUndefined(tokenId)) {
      return this.$q.reject('The tokenId parameter is missing');
    }

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .post(`${this.spynlUrl}${tenantSection}/tokens/revoke`, {
        token: tokenId,
        // TODO CORE-440 move error to catch
      })
      .catch(this.standardErrorParser)
      .then(
        (response) => response.data,
        (error) => this.$q.reject(error.data.message),
      );
  }

  /**
   *
   * Return tokens available for the tenant & matching by filter. Data is
   * paginated by default due to large data sets.
   * @param {object} filter
   * filter object, as being sent to {@link
   * http://docs.mongodb.org/manual/reference/method/db.collection.find/
   * mongo's find function}.
   * @param {int} page
   * page counter. Only natural numbers including 0 are valid. Default 0 - first page.
   * @param {int} limit
   * page size. Default 100
   * @param {array} fields
   * Fields the result should return
   * @param {array} sort
   * Fields it should sort on ex: [['name', 1]] or [['name', -1]] for ascending or descending
   * @param {string} tenantId
   * Optional tenantId
   * @returns {Promise<T>} resolve with data array, or fails with error description.
   */
  filter(
    filter: object,
    page?: number,
    limit?: number,
    fields?: string[],
    sort?: any[],
    tenantId?: string,
  ): ng.IPromise<any> {
    let tenantSection = '';
    limit = angular.isDefined(limit) ? limit : 100;
    page = angular.isDefined(page) ? page : 0;
    fields = angular.isDefined(fields) ? fields : undefined;
    sort = angular.isDefined(sort) ? sort : undefined;

    if (angular.isString(tenantId)) {
      tenantSection = '/tenants/' + tenantId;
    }

    return this.$http
      .post(`${this.spynlUrl}${tenantSection}/tokens/get`, {
        limit: limit,
        skip: page * limit,
        filter: filter,
        fields: fields,
        sort: sort,
      })
      .catch(this.standardErrorParser)
      .then(
        (response: any) => {
          return response.data.data;
          // TODO CORE-440 move to catch
        },
        (error) => this.$q.reject(error.data.message),
      );
  }
}
